import React from 'react';
import styled from 'styled-components';
import { UnstyledLink } from '@zocdoc-frontend-common/components-link';
import { SignInWithEmail } from '@zocdoc-frontend-common/components-sign-in-with-email';
import {
    withCustomMetrics,
    EVENT_INITIATORS,
    INTERACTION_TYPES,
} from '@zocdoc-frontend-common/utils-frontend-metrics';
import { useEffectOnce } from '@zocdoc-frontend-common/utils-react/dist';
import { blue100, slate50 } from '@zocdoc-mezzanine/foundations-colors';
import {
    headingLarge,
    paragraphSmall,
    labelSmall,
} from '@zocdoc-mezzanine/foundations-typography';
import { Metrics } from './constants';
import { SocialLoginButtons } from './SocialLoginButtons';

/**
 *
 * @param {string} facebookLoginUrl
 * @param {string} googleLoginUrl
 * @param {string} appleLoginUrl
 * @param {function} trackClickFacebookLogin
 * @param {function} trackClickGoogleLogin
 * @param {function} trackClickAppleLogin
 * @param {function} trackClickSignUp
 * @param {string} returnUrl
 * @param {boolean} rememberMe
 * @param {boolean} showRememberMe
 * @param {function} onSubmitEmail
 * @param {function} sendMetric
 * @returns {JSX.Element}
 */
const BaseEmailFormView = ({
    facebookLoginUrl,
    googleLoginUrl,
    appleLoginUrl,
    trackClickFacebookLogin,
    trackClickGoogleLogin,
    trackClickAppleLogin,
    trackClickSignUp,
    returnUrl,
    rememberMe,
    showRememberMe,
    onSubmitEmail,
    sendMetric,
}) => {
    useEffectOnce(() => {
        sendMetric({
            action: Metrics.ViewEmailForm,
            element: 'EmailFormView',
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.VIEW,
        });
    });

    return (
        <Wrapper>
            <Header>To log in, enter your email address</Header>
            <SignInWithEmail onSubmitEmail={onSubmitEmail} />
            <Divider />
            <SocialLoginButtons
                baseFacebookLoginUrl={facebookLoginUrl}
                baseGoogleLoginUrl={googleLoginUrl}
                baseAppleLoginUrl={appleLoginUrl}
                trackClickFacebookLogin={trackClickFacebookLogin}
                trackClickGoogleLogin={trackClickGoogleLogin}
                trackClickAppleLogin={trackClickAppleLogin}
                returnUrl={returnUrl}
                rememberMe={rememberMe}
                showRememberMe={showRememberMe}
            />
            <SignUpSection
                trackClickSignUp={trackClickSignUp}
                sendMetric={sendMetric}
            />
        </Wrapper>
    );
};

const Divider = () => {
    return (
        <DividerWrapper>
            <DividerHorizontalLine />
            <DividerText>or</DividerText>
            <DividerHorizontalLine />
        </DividerWrapper>
    );
};

const SignUpSection = ({ trackClickSignUp, sendMetric }) => {
    const href =
        '/createuser/details?referrerType=EmailFirstSignInForm&component=EmailFormView';

    const handleClickSignUp = () => {
        trackClickSignUp();
        sendMetric({
            action: Metrics.ClickCreateAccount,
            element: 'CreateAccountLink',
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.CLICK,
        });
    };

    return (
        <SignupText>
            {'New to Zocdoc? '}
            <SignupLink
                beforeClick={handleClickSignUp}
                href={href}
                data-test={'sign-up-form-sign-up-link'}
            >
                {'Create an account'}
            </SignupLink>
        </SignupText>
    );
};

export const EmailFormView = withCustomMetrics({
    component: 'EmailFormView',
})(BaseEmailFormView);

const Wrapper = styled.div`
    color: ${blue100};
`;

const Header = styled.h1`
    ${headingLarge};
    margin-bottom: 24px;
`;

const DividerWrapper = styled.div`
    align-items: center;
    display: flex;
    margin: 20px 0;
`;

const DividerText = styled.div`
    ${paragraphSmall};
    margin: 0 11px;
`;

const DividerHorizontalLine = styled.div`
    border-bottom: 1px solid ${slate50};
    flex-grow: 1;
`;

const SignupText = styled.div`
    text-align: center;
    margin-top: 20px;
    ${paragraphSmall};
`;

const SignupLink = styled(UnstyledLink)`
    ${labelSmall};
    text-decoration: underline;
    cursor: pointer;
`;
